import type { ScreenRect } from "@msb/reader-bridge";
import { HIGHLIGHT_COLORS, type HighlightColor } from "../annotations.ts";
import { defineMessages, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";

interface Props {
  rect: ScreenRect;
  onColor(color: HighlightColor): void;
  onNote(): void;
  /** Absent quand rien ne peut répondre (ni chat, ni dictionnaire). */
  onExplain?: () => void;
  /** « Traduire » : absent quand le reader ne le propose pas. */
  onTranslate?: () => void;
}

const messages = defineMessages(
  {
    toolbar: "Annoter le texte sélectionné",
    explain: "Explique-moi",
    translate: "Traduire",
    green: "Surligner en vert",
    blue: "Surligner en bleu",
    pink: "Surligner en rose",
    note: "Note",
  },
  {
    toolbar: "Annotate the selected text",
    explain: "Explain this",
    translate: "Translate",
    green: "Highlight in green",
    blue: "Highlight in blue",
    pink: "Highlight in pink",
    note: "Note",
  },
);

const WIDTH = 192;
const EXPLAIN_WIDTH = 56;
const TRANSLATE_WIDTH = 56;
const HEIGHT = 52;

/** Au-dessus du texte sélectionné (en dessous faute de place) : expliquer, traduire, les surligneurs et la note, comme dans Studio. */
export function SelectionToolbar({ rect, onColor, onNote, onExplain, onTranslate }: Props) {
  const t = useT(messages);
  const width = WIDTH + (onExplain ? EXPLAIN_WIDTH : 0) + (onTranslate ? TRANSLATE_WIDTH : 0);
  const left = Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), Math.max(8, window.innerWidth - width - 8));
  // Sur écran tactile, le menu système est au-dessus de la sélection et ses poignées dessous : la barre va sous les poignées.
  const touch = window.matchMedia?.("(pointer: coarse)").matches;
  const above = rect.top - HEIGHT - 8;
  const below = rect.top + rect.height + (touch ? 44 : 8);
  const top = touch ? (below + HEIGHT <= window.innerHeight - 8 ? below : Math.max(8, above - 56)) : above >= 8 ? above : below;
  return (
    <div class="msb-selection-toolbar" role="toolbar" aria-label={t("toolbar")} style={{ left: `${left}px`, top: `${top}px` }}>
      {onExplain && (
        <button type="button" class="msb-selection-toolbar__note" onClick={onExplain} aria-label={t("explain")} title={t("explain")}>
          <Icon name="help" size={28} />
        </button>
      )}
      {onTranslate && (
        <button type="button" class="msb-selection-toolbar__note" onClick={onTranslate} aria-label={t("translate")} title={t("translate")}>
          <Icon name="translate" size={26} />
        </button>
      )}
      {HIGHLIGHT_COLORS.map((color) => (
        <button
          key={color.id}
          type="button"
          class="msb-swatch"
          style={{ background: color.tint }}
          aria-label={t(color.id)}
          title={t(color.id)}
          onClick={() => onColor(color.id)}
        />
      ))}
      <button type="button" class="msb-selection-toolbar__note" onClick={onNote} aria-label={t("note")} title={t("note")}>
        <Icon name="note" size={28} />
      </button>
    </div>
  );
}
